import React, { useState } from 'react';
import clsx from 'clsx';
import * as PropTypes from 'prop-types';
import {
	isNullOrUndefined,
	isNumber,
	isObject,
	isString,
	useDeepMemoize,
	useObserveRef,
} from '@chkp/mwc-common';
import { Cell } from './Cell';
import styles from './Row.scss';

// ------------------------------------------------
// Detect when cell is hovered and pass 'hover' prop to cellRenderer
// Note: direct onMouseEnter & onMouseLeave is unreliable -
// https://github.com/facebook/react/issues/4492
//
const useRowHover = (hoverObservable, rowKey) => {
	const [hover, setHover] = useState(false);
	useObserveRef(hoverObservable, (details) =>
		setHover(details && details.rowKey === rowKey),
	);

	return hover;
};
// ------------------------------------------------

/**
 * Resolve row-context from grid's shared-data and row index
 * @param cellProps
 * @returns {Object}
 */
const useRowContext = (rowProps) => {
	const {
		data, // grid's shared data for all rows
		index, // row idx
		style, // positioning (provided by virtualization)
		isScrolling, // Indicates the grid is being scrolled / resized. Intended for optimizations
	} = rowProps;

	// Same data is passed to each row (itemData in Virtualized.js)
	const {
		rowRenderer,
		getRowKeyByIdx,
		columnsObservable,
		scrollXObservable,
		hoverObservable,
		onClick,
		onDoubleClick,
		onMouseDown,
		context,
		contentWidthObservable,
		border,
		background,
		rowHover,
		cellBorders,
		striped,
	} = data;

	const { height } = style;

	const key = getRowKeyByIdx(index);
	const hover = useRowHover(hoverObservable, key);

	// Do not include 'top' in rowContext in order to keep the context memoized during vertical scroll
	const rowContext = {
		index,
		key,
		columnsObservable,
		scrollXObservable,
		contentWidthObservable,
		hoverObservable,
		onClick,
		onDoubleClick,
		onMouseDown,
		context,
		height,
		border,
		background,
		cellBorders,
		rowHover,
		striped,
		isScrolling,
		hover,
	};

	try {
		const mod = rowRenderer && rowRenderer(rowContext);

		if (React.isValidElement(mod) || isString(mod) || isNumber(mod)) {
			rowContext.content = mod;
		} else if (isObject(mod)) {
			Object.assign(rowContext, mod);
		}
	} catch (e) {
		console.error('Row renderer error: ', rowContext, e);
		rowContext.content = '';
	}

	return useDeepMemoize(rowContext);
};

const Row = (props) => {
	const {
		style: { top },
		index,
	} = props;

	const rowContext = useRowContext(props);
	const {
		className,
		style,
		columnsObservable,
		scrollXObservable,
		contentWidthObservable,
		content,
		locked,
		onClick,
		onDoubleClick,
		onMouseDown,
		selected,
		height,
		border,
		background,
		indent = 0,
		rowHover,
		striped,
		wrapper,
		// DataGrid should provide row keys via internal rowRenderer.
		// As a fallback, use row index
		key = `___${index}`,
	} = rowContext;

	const _className = clsx(
		styles.Row,
		background && styles.withBackground,
		striped && index % 2 && styles.stripedOdd,
		border && styles.withBorder,
		rowHover && styles.withHover,
		selected && styles.selected,
		className,
	);

	const _style = {
		...style,
		top,
		height,
		left: scrollXObservable.current + indent,
		width: contentWidthObservable.current - indent,
	};

	const columns = columnsObservable.current;
	const scrollX = scrollXObservable.current;

	let _content;
	if (isNullOrUndefined(content)) {
		if (columns) {
			_content = columns.map((column) => (
				<Cell key={column.key} column={column} rowContext={rowContext} />
			));
			const offsetterIdx = columns.findIndex((col) => !col.locked);

			// Insert an element which offsets all the cells after it. this offset is synchronized with horizontal scroll
			_content.splice(
				offsetterIdx,
				0,
				<div
					key='__offsetter'
					style={{
						display: 'inline-block',
						marginLeft: -scrollX,
					}}
				/>,
			);
		}
	} else {
		_content = content;
	}

	let ret = (
		<div
			role={'row'}
			className={_className}
			style={_style}
			onClick={onClick}
			onDoubleClick={onDoubleClick}
			onMouseDown={onMouseDown}
			data-key={key}
			data-index={index}
			data-locked={!!locked}
			data-custom-content={!!content}
			data-indent={indent}
			aria-rowindex={index}
		>
			{_content}
		</div>
	);

	// Let renderer to wrap or manipulate cell element
	if (wrapper) {
		ret = wrapper(ret);
	}

	return ret;
};
Row.propTypes = {
	/* eslint-disable react/no-unused-prop-types */
	data: PropTypes.object,
	index: PropTypes.number,
	style: PropTypes.object,
};

export { Row };
